import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Lock, Key, CheckCircle, AlertCircle, Eye, EyeOff,
  User, Phone, Globe, Mail, Save, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiConfig';

export default function ProfileSecuritySection() {
  const { user, token, refreshUser } = useAuth();
  const { language, t } = useLanguage();
  
  // Profile state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Password state
  const [hasPassword, setHasPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Load user profile
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const u = data.user || {};
          setFirstName(u.first_name || '');
          setLastName(u.last_name || '');
          setPhone(u.phone || '');
          setPreferredLanguage(u.preferred_language || language);
          setHasPassword(data.has_password || false);
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      }
    };
    if (token) loadProfile();
  }, [token, language]);
  
  // Save profile
  const handleSaveProfile = async () => {
    setProfileLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
          phone: phone.trim() || null,
          preferred_language: preferredLanguage
        })
      });
      
      if (res.ok) {
        toast.success(language === 'es' ? 'Perfil actualizado' : 'Profile updated');
        if (refreshUser) refreshUser();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update profile');
      }
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };
  
  // Create password
  const handleCreatePassword = async () => {
    if (newPassword.length < 8) {
      toast.error(language === 'es' ? 'La contraseña debe tener al menos 8 caracteres' : 'Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/create-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: newPassword,
          confirm_password: confirmPassword
        })
      });
      
      if (res.ok) {
        toast.success(language === 'es' ? '¡Contraseña creada! Ahora puede iniciar sesión con su correo y contraseña.' : 'Password created! You can now sign in with your email and password.');
        setHasPassword(true);
        setShowPasswordForm(false);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to create password');
      }
    } catch (err) {
      toast.error('Failed to create password');
    } finally {
      setPasswordLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Profile Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-slate-200 p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <User className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">
              {language === 'es' ? 'Información del Perfil' : 'Profile Information'}
            </h3>
            <p className="text-sm text-slate-500">
              {language === 'es' ? 'Actualiza tu información personal' : 'Update your personal information'}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firstName" className="text-slate-700">
              {language === 'es' ? 'Nombre' : 'First Name'}
            </Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={language === 'es' ? 'Tu nombre' : 'Your first name'}
              className="mt-1"
              data-testid="profile-first-name"
            />
          </div>
          
          <div>
            <Label htmlFor="lastName" className="text-slate-700">
              {language === 'es' ? 'Apellido' : 'Last Name'}
            </Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={language === 'es' ? 'Tu apellido' : 'Your last name'}
              className="mt-1"
              data-testid="profile-last-name"
            />
          </div>
          
          <div>
            <Label htmlFor="phone" className="text-slate-700">
              <Phone className="w-4 h-4 inline mr-1" />
              {language === 'es' ? 'Teléfono' : 'Phone'}
            </Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 123-4567"
              className="mt-1"
              data-testid="profile-phone"
            />
          </div>
          
          <div>
            <Label htmlFor="language" className="text-slate-700">
              <Globe className="w-4 h-4 inline mr-1" />
              {language === 'es' ? 'Idioma Preferido' : 'Preferred Language'}
            </Label>
            <select
              id="language"
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="profile-language"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>
        </div>
        
        {/* Email (read-only) */}
        <div className="mt-4">
          <Label className="text-slate-700">
            <Mail className="w-4 h-4 inline mr-1" />
            {language === 'es' ? 'Correo Electrónico' : 'Email'}
          </Label>
          <div className="mt-1 flex items-center gap-2">
            <Input
              value={user?.email || ''}
              disabled
              className="bg-slate-50"
            />
            <span className="text-xs text-emerald-600 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {language === 'es' ? 'Verificado' : 'Verified'}
            </span>
          </div>
        </div>
        
        <div className="mt-6">
          <Button
            onClick={handleSaveProfile}
            disabled={profileLoading}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="profile-save-btn"
          >
            {profileLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {language === 'es' ? 'Guardar Cambios' : 'Save Changes'}
          </Button>
        </div>
      </motion.div>
      
      {/* Security Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl border border-slate-200 p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">
              {language === 'es' ? 'Seguridad de la Cuenta' : 'Account Security'}
            </h3>
            <p className="text-sm text-slate-500">
              {language === 'es' ? 'Gestiona tu método de inicio de sesión' : 'Manage your sign-in method'}
            </p>
          </div>
        </div>
        
        {/* Current Auth Method */}
        <div className="bg-slate-50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${hasPassword ? 'bg-emerald-100' : 'bg-blue-100'}`}>
                {hasPassword ? (
                  <Lock className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Key className="w-4 h-4 text-blue-600" />
                )}
              </div>
              <div>
                <p className="font-medium text-slate-900">
                  {hasPassword 
                    ? (language === 'es' ? 'Contraseña Configurada' : 'Password Set')
                    : (language === 'es' ? 'Enlace Mágico' : 'Magic Link')
                  }
                </p>
                <p className="text-sm text-slate-500">
                  {hasPassword
                    ? (language === 'es' ? 'Puedes iniciar sesión con tu correo y contraseña' : 'You can sign in with your email and password')
                    : (language === 'es' ? 'Recibes un enlace por correo para iniciar sesión' : 'You receive a sign-in link via email')
                  }
                </p>
              </div>
            </div>
            {hasPassword && (
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            )}
          </div>
        </div>
        
        {/* Add Password Option (for magic-link users) */}
        {!hasPassword && !showPasswordForm && (
          <div className="border border-dashed border-slate-300 rounded-xl p-4 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-slate-900">
                  {language === 'es' ? 'Asegura Tu Cuenta' : 'Secure Your Account'}
                </h4>
                <p className="text-sm text-slate-600 mt-1">
                  {language === 'es' 
                    ? 'Añade una contraseña para más opciones de inicio de sesión. Podrás seguir usando el enlace mágico también.'
                    : 'Add a password for more sign-in options. You can still use magic link too.'
                  }
                </p>
                <Button
                  onClick={() => setShowPasswordForm(true)}
                  className="mt-3 bg-purple-600 hover:bg-purple-700"
                  data-testid="add-password-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Añadir Contraseña' : 'Add Password'}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Password Creation Form */}
        {!hasPassword && showPasswordForm && (
          <div className="border border-purple-200 rounded-xl p-4 bg-purple-50">
            <h4 className="font-semibold text-slate-900 mb-4">
              {language === 'es' ? 'Crear Contraseña' : 'Create Password'}
            </h4>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="newPassword">
                  {language === 'es' ? 'Nueva Contraseña' : 'New Password'}
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={language === 'es' ? 'Mínimo 8 caracteres' : 'Minimum 8 characters'}
                    className="pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">
                  {language === 'es' ? 'Confirmar Contraseña' : 'Confirm Password'}
                </Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={language === 'es' ? 'Repite la contraseña' : 'Repeat password'}
                  className="mt-1"
                  data-testid="confirm-password-input"
                />
              </div>
              
              {newPassword.length > 0 && newPassword.length < 8 && (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {language === 'es' ? 'La contraseña debe tener al menos 8 caracteres' : 'Password must be at least 8 characters'}
                </p>
              )}
              
              <div className="flex gap-3">
                <Button
                  onClick={handleCreatePassword}
                  disabled={passwordLoading || newPassword.length < 8 || newPassword !== confirmPassword}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="create-password-btn"
                >
                  {passwordLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  {language === 'es' ? 'Crear Contraseña' : 'Create Password'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Password already set message */}
        {hasPassword && (
          <div className="text-sm text-slate-500 mt-2">
            {language === 'es' 
              ? 'Tu cuenta está protegida con una contraseña. Puedes iniciar sesión con tu correo y contraseña o con un enlace mágico.'
              : 'Your account is protected with a password. You can sign in with your email and password or with a magic link.'
            }
          </div>
        )}
      </motion.div>
    </div>
  );
}
